import { graphql } from '@/gql';
import type {
  DeliveryAddressInputType,
  EstimateAdditionalChargesInput,
  GetEstimateQuery,
  GourmetChargeOptionsInput,
  PromotionInput,
  StoreItemDetailsInput,
} from '@/gql/graphql';
import { queryExecute } from '@/Utils/graphqlAPI';

export type GourmetGetEstimateInput = {
  callerId: string;
  items: StoreItemDetailsInput;
  address: DeliveryAddressInputType;
  additionalCharges: EstimateAdditionalChargesInput;
  chargeOptions?: GourmetChargeOptionsInput;
  promotion?: PromotionInput;
};

export type GourmetGetEstimateOutput = GetEstimateQuery['getEstimate'];

export const gourmetGetEstimate = async ({
  callerId,
  items,
  address,
  additionalCharges,
  chargeOptions,
  promotion,
}: GourmetGetEstimateInput) => {
  const response = await queryExecute({
    query: graphql(`
      query getEstimate(
        $callerId: String!
        $items: [StoreItemDetailsInput!]!
        $address: DeliveryAddressInputType!
        $additionalCharges: EstimateAdditionalChargesInput!
        $chargeOptions: GourmetChargeOptionsInput
        $promotion: PromotionInput
      ) {
        getEstimate(
          callerId: $callerId
          items: $items
          address: $address
          additionalCharges: $additionalCharges
          chargeOptions: $chargeOptions
          promotion: $promotion
        ) {
          noAsapDelivery
          total
          storeId
          menuId
          charges {
            title
            value
            originalValue
            tooltips {
              title
              value
            }
          }
          service
          timeEstimate {
            min
            max
          }
          userCharges {
            credits
            card
            expiringCredits
          }
          accountId
          currency
          serviceEstimateReference
          orderOptions {
            storeInstructions
            deliveryInstructions
            dropoffOptionId
          }
          address {
            placeId
            latitude
            longitude
            name
            formattedAddress
            streetNumber
            postalCode
            streetAddress
            streetAddress2
            state
            city
            country
          }
          additionalCharges {
            tip
            serviceFee
            shouldTipBeSubsidized
          }
          chargeOptions {
            plan
            tier
            applyDiscountedFee
            noServiceFee
            doNotCharge
            chargeNow
            reason
          }
          callSid
          promotion {
            promotionUuid
            text
            description
          }
          isSpecialInstructionsDisabled
          items {
            item {
              id
              storeId
              storeTitle
              title
              subtitle
              service
              price
              quantity
              currency
              specialInstructions
              serviceItemReference
              substitutionPreference
            }
            options {
              ...OptionsFields
              values {
                ...ValuesFields
                subOptions {
                  ...OptionsFields
                  values {
                    ...ValuesFields
                    subOptions {
                      ...OptionsFields
                      values {
                        ...ValuesFields
                        subOptions {
                          ...OptionsFields
                          values {
                            ...ValuesFields
                            subOptions {
                              ...OptionsFields
                              values {
                                ...ValuesFields
                                subOptions {
                                  ...OptionsFields
                                  values {
                                    ...ValuesFields
                                    subOptions {
                                      ...OptionsFields
                                      values {
                                        ...ValuesFields
                                        subOptions {
                                          ...OptionsFields
                                          values {
                                            ...ValuesFields
                                            subOptions {
                                              ...OptionsFields
                                              values {
                                                ...ValuesFields
                                                subOptions {
                                                  ...OptionsFields
                                                  values {
                                                    ...ValuesFields
                                                  }
                                                }
                                              }
                                            }
                                          }
                                        }
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      fragment OptionsFields on ItemOptionsType {
        id
        title
        subtitle
        max
        min
        type
        minAggregateOptionsQuantity
        maxAggregateOptionsQuantity
        selectedValues
      }
      fragment ValuesFields on ItemOptionsValuesType {
        id
        title
        subtitle
        price
        max
        min
        default
        nextCursor
      }
    `),
    variables: {
      callerId,
      items,
      address,
      additionalCharges,
      chargeOptions,
      promotion,
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  return response;
};
